import { Company } from "@/components/CompanyCard";
import { Building2, Star } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { EntityList } from "@/components/common/EntityList";
import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useMultiSelect } from "@/hooks/useMultiSelect";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { DuplicateManager, useDuplicateManager } from "@/components/common";
import { companyMergeConfig } from "@/config/companyMergeConfig";
import { EntityMergeService } from "@/services/entityMergeService";
import { toast } from "@/hooks/use-toast";
import { CompanyRelationshipBadge } from "./CompanyRelationshipBadge";

interface CompaniesListProps {
  companies: Company[];
  filteredCompanies: Company[];
  isLoading: boolean;
  onDeleteCompanies?: (ids: string[]) => Promise<void>;
  onCompaniesChange?: () => void;
  favorites?: Set<string>;
}

export const CompaniesList = ({
  companies,
  filteredCompanies,
  isLoading,
  onDeleteCompanies,
  onCompaniesChange,
  favorites = new Set()
}: CompaniesListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const {
    isMultiSelectMode,
    toggleMultiSelect,
    toggleItemSelection,
    handleDeleteSelected,
    isSelected,
    selectedCount,
    selectAll
  } = useMultiSelect(filteredCompanies, onDeleteCompanies || (() => Promise.resolve()));

  const {
    openMergeModal,
    getDuplicateManagerProps
  } = useDuplicateManager(filteredCompanies, isSelected, "companies", companyMergeConfig);

  // Handle company merge
  const handleMerge = async (merged: Company, sources: Company[]) => {
    try {
      const result = await EntityMergeService.mergeCompanies(merged, sources);
      
      if (result.success) {
        toast({
          title: "Companies merged successfully",
          description: `${sources.length} companies have been merged into one.`,
        });
        // Refresh companies list
        if (onCompaniesChange) {
          onCompaniesChange();
        }
        if (result.redirectPath)
          navigate(result.redirectPath);
        return result;
      } else {
        toast({
          title: "Failed to merge companies",
          description: result.error || "An error occurred during merge.",
          variant: "destructive",
        });
        // Rethrow error to prevent modal from closing
        throw new Error(result.error || "An error occurred during merge.");
      }
    } catch (error) {
      console.error("Merge error:", error);
      toast({
        title: "Failed to merge companies",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
      // Rethrow error to prevent modal from closing
      throw error;
    }
  };

  const columns = [
    {
      key: "name",
      header: t("companies.header"),
      cell: (company: Company) => (
        <Link to={`/companies/${company.id}`} className="hover:underline flex items-center gap-3">
          <div className="w-8 h-8 flex-shrink-0">
            {company.logo_url ? (
              <img 
                src={company.logo_url} 
                alt={company.name} 
                className="w-8 h-8 rounded object-contain bg-white dark:bg-gray-800 border p-0.5"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div 
              className={cn(
                "w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center text-digger-blue",
                company.logo_url && "hidden"
              )}
            >
              <Building2 className="h-4 w-4" />
            </div>
          </div>
          <span className="truncate">{company.name}</span>
          {favorites.has(company.id) && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 ml-1 flex-shrink-0" />}
        </Link>
      ),
    },
    {
      key: "relationship",
      header: t("companies.relationship.label"),
      cell: (company: Company) => (
        <CompanyRelationshipBadge relationship={company.relationship} size="sm" />
      ),
    },
    {
      key: "jobCount",
      header: t("companies.jobs"),
      cell: (company: Company) => (
        <div
          className="flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold"
          style={{ background: 'rgba(59, 130, 246, 0.03)', color: '#2563eb' }}
        >
          <span>{company.jobCount ?? 0}</span>
          <span className="ml-1">{company.jobCount === 1 ? t("companies.job") : t("companies.jobs")}</span>
        </div>
      ),
    },
  ];

  return (
    <>
      <EntityList
        data={filteredCompanies}
        columns={columns}
        isLoading={isLoading}
        emptyMessage={t("companies.empty")}
        basePath="/companies"
        isMultiSelectMode={isMultiSelectMode}
        onToggleMultiSelect={toggleMultiSelect}
        onToggleItemSelection={toggleItemSelection}
        isItemSelected={isSelected}
        onDeleteSelected={() => setShowDeleteDialog(true)}
        onMergeSelected={openMergeModal}
        selectedCount={selectedCount}
        selectAll={selectAll}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("common.deleteConfirmation", { count: selectedCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteSelected();
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DuplicateManager
        {...getDuplicateManagerProps(handleMerge)}
      />
    </>
  );
};
